import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { GetPublicShowroomFieldsQuery } from 'generated/graphql';

import { getVersion } from 'lib/cloudinary';
import { UserConsentHookResponse } from 'lib/userConsent';

import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CircularProgress from '@material-ui/core/CircularProgress';
import Container from '@material-ui/core/Container';
import TextField from '@material-ui/core/TextField';

import VideoPlayer from 'components/Common/VideoPlayer';

import { useStyles } from './styles';

export type PasswordBackground = {
	type: string;
	src: string;
};

export type ComponentProps = {
	publicFields: GetPublicShowroomFieldsQuery['publicShowroomFields'];
	background: PasswordBackground;
	email: string;
	password: string;
	loading: boolean;
	wrongEmail: boolean;
	wrongPassword: boolean;
	renderUserConsentInput: UserConsentHookResponse['renderInput'];
	onChangeEmail(email: string): void;
	onChangePassword(password: string): void;
	onSubmit(): void;
};

const ShowroomLogin = (props: ComponentProps) => {
	const { publicFields, background, email, password, loading, wrongEmail, wrongPassword } = props;
	const { onChangeEmail, onChangePassword, onSubmit, renderUserConsentInput } = props;
	const classes = useStyles();
	const { t } = useTranslation();

	return (
		<Container fixed className={classes.container}>
			{background.type === 'video' ? (
				<VideoPlayer
					id="showroomLoginBackgroundVideo"
					videoId={background.src}
					cloudinaryVersion={getVersion(background.src)}
					config={{
						autoPlay: true,
						muted: true,
						fullScreen: true,
						preview: false,
						loop: true,
						controls: false
					}}
				/>
			) : (
				<>
					{!publicFields.heroMedia && !publicFields.defaultBackground ? <div className={classes.blackLine}></div> : null}
					<div
						className={clsx(classes.backgroundImage, {
							[classes.backgroundLogo]: !publicFields.heroMedia && !publicFields.defaultBackground
						})}
						style={{ backgroundImage: `url(${background.src}` }}
					/>
				</>
			)}
			<Card
				className={clsx(classes.card, {
					[classes.centeredCard]: !publicFields.heroMedia && !publicFields.defaultBackground
				})}
			>
				<CardContent className={classes.cardContentRoot}>
					<div className={classes.cardHeader}>
						<img className={classes.cardLogo} src={publicFields.logo || '/logo.svg'} alt="Galleries" />
						<div className={classes.cardTitle}>{publicFields.librarySeason}</div>
						<div className={classes.cardTitle}>{publicFields.libraryCollection}</div>
					</div>
					<div className={classes.cardContent}>
						<div className={classes.cardContentTitle}>{t('showroom.private.card.title')}</div>
						<div className={classes.cardContentSubTitle}>{t('showroom.private.card.subTitle')}</div>
						<form
							id="showroomPasswordForm"
							onSubmit={ev => {
								ev.preventDefault();
								onSubmit();
							}}
						>
							<div className={classes.cardContentInputBox}>
								<TextField
									id="showroomEmailInput"
									className={classes.cardContentInput}
									inputProps={{
										className: classes.cardContentInputItem
									}}
									fullWidth
									error={wrongEmail}
									variant="outlined"
									onChange={ev => onChangeEmail(ev.target.value)}
									value={email}
									placeholder={t('showroom.private.card.form.email.placeholder')}
								/>
							</div>
							<div className={classes.cardContentInputBox}>
								<TextField
									id="showroomPasswordInput"
									className={classes.cardContentInput}
									inputProps={{
										className: classes.cardContentInputItem
									}}
									fullWidth
									error={wrongPassword}
									helperText={wrongEmail || wrongPassword ? t('showroom.private.card.form.password.error') : ''}
									variant="outlined"
									value={password}
									onChange={ev => onChangePassword(ev.target.value)}
									placeholder={t('showroom.private.card.form.password.placeholder')}
								/>
							</div>
							{renderUserConsentInput({
								wrapper: {
									classes: {
										root: classes.cardContentInputBox
									}
								},
								label: {
									classes: {
										root: classes.privacyPolicyLabelRoot
									}
								}
							})}
							<div>
								{loading ? (
									<CircularProgress />
								) : (
									<Button
										id="submitPasswordButton"
										color="primary"
										variant="contained"
										type="submit"
										disabled={!password || wrongEmail}
									>
										{t('showroom.private.card.form.button')}
									</Button>
								)}
							</div>
						</form>
					</div>
				</CardContent>
			</Card>
		</Container>
	);
};

export default React.memo(ShowroomLogin);
